import type {
	I2VAspectRatio,
	I2VModelId,
	I2VResolution,
} from "@saas/image-to-video/types";

/**
 * /burning-bridges-ai-video-generator 的预设：一张照片（人或宠物）+ 吊灯包厢场景图 → Drake
 * 《Burning Bridges》MV 那段派对的原创版（2026-10-11）。方案见
 * docs/research/burning-bridges-ai-research-2026-10-11.md。
 * 这里只放 type import，apps/web/test 的 transpile loader 才能直接加载。
 */
export const BURNING_BRIDGES_PAGE_PATH = "/burning-bridges-ai-video-generator";

/** 登录前现场的 IndexedDB key，和 /image-to-video、首页、其他预设页分开 */
export const BURNING_BRIDGES_PENDING_STATE_KEY = "pendingBurningBridgesState";

/**
 * 第二槽的默认场景图（gpt-image-2 生成的木墙包厢 + 水晶吊灯 + 举银杯的虚构人群）。
 * Grok 单图时上游不收 aspect_ratio（输出跟原图比例），两张图才认 9:16；四个模型实测都照这张图出场景
 */
export const BURNING_BRIDGES_PARTY_PATH =
	"home/showcase/burning-bridges/refs/party-room.jpg";

/**
 * Me 模板 Standard / HD 的动作参考（2026-10-11 晚老公定）：原 MV 去音轨、本地
 * Depth Anything V2 Small 转的灰度深度图，没有人脸也没有原曲（原 MV 直接喂会被 Seedance 当真人脸
 * 拒单；Starrd 也是喂深度图）。放在用户上传那个桶 images-art-prod：后端只收
 * NEXT_PUBLIC_R2_PUBLIC_URL 下的参考视频。14.917 秒计 15 秒，Mini 480p 72 / 720p 150 积分。
 * 实测见 docs/research/burning-bridges-ai-research-2026-10-11.md §3。
 * 2026-10-12 起服务端认这个 key，出片后把音轨换成原 MV 同一段原曲（packages/api 的 preset-audio.ts）。
 * v1 截 4.5–19.4s，原曲正好在 "where she at?" 开口前断掉；v2 改截 7.5–22.4s，两句在 12.3s / 14.1s，
 * 丢了开头冲镜头弹指那 3 秒
 */
export const BURNING_BRIDGES_MOTION_PATH =
	"presets/burning-bridges/motion-depth-v2.mp4";
export const BURNING_BRIDGES_MOTION_SECONDS = 14.917;
/** 原 MV 是横的，带深度图时比例锁 16:9（竖版没测过） */
export const BURNING_BRIDGES_MOTION_ASPECT_RATIO: I2VAspectRatio = "16:9";

export type BurningBridgesQualityId = "lite" | "standard" | "hd";

/**
 * 2026-10-11 老公定：Lite 走 Grok 吃免费额度（6 秒 14 积分，15 免费积分刚好一条）；
 * Standard / HD 走 Seedance 2.0 Mini 15 秒（宠物 57 / 123 积分；Me 带深度图参考视频 72 / 150），同 Kavel。
 * 实测 Seedance 2.0 Fast（176）、Wan 3.0（120）、2.0（285）、2.5（420）一图出片不比 Mini 好多少，
 * 老公嫌贵不换。时长跟着档位固定
 */
export const BURNING_BRIDGES_QUALITIES: {
	id: BurningBridgesQualityId;
	model: I2VModelId;
	resolution: I2VResolution;
	duration: number;
}[] = [
	{ id: "lite", model: "fast-3.0", resolution: "480p", duration: 6 },
	{
		id: "standard",
		model: "seedance-2-mini",
		resolution: "480p",
		duration: 15,
	},
	{ id: "hd", model: "seedance-2-mini", resolution: "720p", duration: 15 },
];

export const BURNING_BRIDGES_PRESET = {
	mode: "reference",
	defaultQualityId: "lite" as BurningBridgesQualityId,
	aspectRatioOptions: ["9:16", "16:9"] as I2VAspectRatio[],
	defaultAspectRatio: "9:16" as I2VAspectRatio,
	durationOptions: [6, 15],
	defaultDuration: 6,
	/** 主角 + 派对场景两个槽位，顺序即上传给模型的图片顺序 */
	slotCount: 2,
} as const;

export type BurningBridgesTemplateId = "me" | "pet";

/** 同其他热梗页：用户只能加一句 director's note，拼到模板后面 */
export const BURNING_BRIDGES_NOTE_PREFIX = "\n\nDirector's note: ";

/** 只看 note 是否全空白，不 trim 内容本身：hero 每敲一个字都会走这里 */
export function buildBurningBridgesPrompt(
	templatePrompt: string,
	note: string,
): string {
	return note.trim()
		? `${templatePrompt}${BURNING_BRIDGES_NOTE_PREFIX}${note}`
		: templatePrompt;
}

/** Lite（Grok 1.5，6 秒）：2026-10-11 直连 kie 实测，走出人群 → 指镜头 → 张臂 → 手搭眉骨张望 */
const ME_LITE_PROMPT = `6-second hip-hop party clip, handheld phone camera, photoreal. PHOTO 1 is the star: keep their exact face, hairstyle, skin tone, build and outfit from PHOTO 1. PHOTO 2 is the room: a packed, wood-panelled private dining room at night under glowing crystal chandeliers, a crowd of friends raising silver cups and cheering. The star holds a silver cocktail shaker in the left hand.
0-2s: the star walks out of the crowd toward the camera with a confident swagger.
2-3.5s: flicks their fingers at the lens and spreads their arms wide.
3.5-6s: puts one open hand flat above their eyes like a visor and scans left and right across the room as if asking "where she at?", then grins at the lens.
Warm amber light, halation off the chandeliers, creamy bokeh, film grain. Original hip-hop beat with crowd cheers, no lyrics, no existing song. No famous people, no text, no logos, no watermark.`;

/**
 * Standard / HD（Seedance 2.0 Mini，15 秒）：动作跟 @Video 1（深度图），不再逐秒写。参考视频和图在
 * kie 那边分开编号，所以这里用 @Video 1 / @Image 1 / @Image 2，不用 PHOTO 1。深度图里是宽肩男人的
 * 轮廓，女生照片实测开头近 1 秒冒出一个陌生男人、袖子串色，所以写死「从第一帧起就是 @Image 1、
 * 身形按照片不按深度图」。动作顺序跟 v2 深度图。Seedance 在站内提示词上限 1000 字，模板 815 字给 note 留 166
 */
const ME_PROMPT = `15-second hip-hop music video, photoreal. @Video 1 is a grayscale depth-map video: use it only for the star's motion, timing and camera path; the output is full colour. @Image 1 is the star from the first frame: keep their exact face, hair, body, gender and outfit, never the body in @Video 1 or anyone else. @Image 2 is the room: a packed wood-panelled party room under crystal chandeliers, with new guests. The star holds a silver metal cup and does the moves from @Video 1: raises the cup, tugs the collar, reaches the cup into the crowd, flicks fingers at the lens, then puts one open hand flat above the eyes like a visor and scans the room as if asking "where she at?". Warm amber light, film grain. Original hip-hop beat, crowd cheers, no lyrics, no existing song. No famous people, text, logos or watermark.`;

/**
 * 宠物单写一套（Rumpelstiltskin 实测：人和宠物共用 prompt 会把人变精灵、把狗当道具）。
 * 动物做不出标准的手搭眉骨，实测是抬爪到眼边
 */
const PET_LITE_PROMPT = `6-second hip-hop party clip, handheld phone camera, photoreal. PHOTO 1 is the star: the exact same animal from PHOTO 1, same species, face, fur color, markings and size. It stays a real animal with real paws, never human hands or a human body. PHOTO 2 is the room: a packed, wood-panelled private dining room at night under glowing crystal chandeliers, a crowd of friends raising silver cups and cheering. The pet stands upright on its hind legs like the host of the party, a small silver cocktail shaker held against its chest with one front paw.
0-2s: the pet struts out of the crowd toward the camera.
2-3.5s: pokes one front paw at the lens, then spreads both front legs wide.
3.5-6s: lifts one front paw flat above its eyes like a visor and looks left and right across the room as if asking "where she at?", then tilts its head at the lens.
Warm amber light, halation off the chandeliers, creamy bokeh, film grain. Original hip-hop beat with crowd cheers, no lyrics, no existing song. No famous people, no text, no logos, no watermark.`;

const PET_PROMPT = `15-second hip-hop music video, handheld phone camera, photoreal. PHOTO 1 is the star: the same animal, same face, fur and markings, with real paws, never human hands. PHOTO 2 is the room: a packed wood-panelled dining room under crystal chandeliers, friends raising silver cups and cheering. The pet stands upright on its hind legs, a silver cocktail shaker held to its chest.
0-3s: struts out of the crowd toward the camera.
3-5s: pokes one front paw at the lens.
5-8s: spreads both front legs wide.
8-11s: tips the shaker over the crowd's raised cups.
11-15s: lifts a front paw above its eyes like a visor and looks around as if asking "where she at?", does it again, tilts its head at the lens.
Warm amber light, film grain. Original hip-hop beat, crowd cheers, no lyrics, no existing song. No text, logos or watermark.`;

/**
 * 不写歌词、不点名歌手和歌名，配乐要原创；画幅不写进 prompt，由比例参数决定。
 * motionReference 的模板在收参考视频的档（Standard / HD）带上 BURNING_BRIDGES_MOTION_PATH。
 * 宠物不带：深度图里的人形会把狗顶掉（实测 4–7 秒主角变成人）。
 * test/burning-bridges-preset.test.mjs 卡住长度和这几条
 */
export const BURNING_BRIDGES_TEMPLATES: {
	id: BurningBridgesTemplateId;
	prompt: string;
	promptByQuality: Partial<Record<BurningBridgesQualityId, string>>;
	motionReference?: boolean;
}[] = [
	{
		id: "me",
		prompt: ME_PROMPT,
		promptByQuality: { lite: ME_LITE_PROMPT },
		motionReference: true,
	},
	{
		id: "pet",
		prompt: PET_PROMPT,
		promptByQuality: { lite: PET_LITE_PROMPT },
	},
];
